/**
 * Message model for the Ask Mozi drawer (docs/internal/MOZI_CHAT_DRAWER.md).
 *
 * `/ai/chat` answers markdown, so `blocks` are derived client-side from that
 * text. With flag `mozi_change_cards` on it may also send `blocks`, an `action`
 * and a `change` proposal, which the drawer renders as a change card (states
 * 1c and 1d) after the text. `changeStatus` and the `change*` fields below are
 * the card's own state, driven by POST /ai/changes/:id/apply and /undo.
 */
export type MessageBlock =
  | { t: 'p'; text: string }
  | { t: 'ol'; items: string[]; /** Source number of the first item, so a list split by a paragraph keeps counting. */ start: number }
  | { t: 'ul'; items: string[] };

export interface MessageAction {
  label: string;
  href: string;
}

export type ChangeType = 'sync_fields' | 'product_price';

/** One row of a change card. Booleans are a sync field switched on or off; numbers are a price. */
export interface ChangeField {
  field: string;
  label?: string;
  from: boolean | number | null;
  to: boolean | number | null;
}

/** The proposal `/ai/chat` sends beside the text (src/types/aiChange.ts `AiChangeProposal`). */
export interface MessageChange {
  id: string;
  type: ChangeType;
  canApply: boolean;
  whoCan: string;
  requiresConfirm: boolean;
  /** True when a before value was captured, so Undo is offered even on a failed apply. */
  canUndo?: boolean;
  secondary: { label: string; href: string };
  payload: {
    integrationId: string;
    integrationName: string | null;
    productId?: string;
    productName?: string | null;
    changes: ChangeField[];
    /** sync_fields: products the next sync writes to. */
    productCount?: number;
    /** ISO time of the next scheduled sync; null when the integration only syncs on demand. */
    nextSyncAt: string | null;
  };
}

export type ChangeStatus = 'proposed' | 'applying' | 'applied' | 'failed' | 'undone';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  text?: string;
  blocks?: MessageBlock[];
  action?: MessageAction;
  change?: MessageChange;
  changeStatus?: ChangeStatus;
  /** Why the last apply or undo did not take effect, as the backend said it. */
  changeError?: string;
  /** The failed response said a before value exists: the card offers Undo beside Retry. */
  changeCanUndo?: boolean;
  /** True only when the backend read the value back and it matched. */
  changeVerified?: boolean;
  /** The price read back from the store after the last apply or undo. */
  changePrice?: number | null;
  loading?: boolean;
  /** The reply failed; the drawer shows the error paragraph and Try again. */
  error?: boolean;
  /** The prompt a failed reply answered, resent by its Try again. */
  prompt?: string;
}

const BULLET = /^[•\-*]\s+(.*)$/;
const NUMBERED = /^(\d+)\.\s+(.+)$/;

/** Group the reply's lines into paragraphs, numbered lists and bullet lists. */
export const toBlocks = (text: string): MessageBlock[] => {
  const blocks: MessageBlock[] = [];
  (text || '').split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const last = blocks[blocks.length - 1];
    const numbered = line.match(NUMBERED);
    if (numbered) {
      if (last && last.t === 'ol') last.items.push(numbered[2]);
      else blocks.push({ t: 'ol', items: [numbered[2]], start: parseInt(numbered[1], 10) });
      return;
    }
    const bullet = line.match(BULLET);
    if (bullet && bullet[1]) {
      if (last && last.t === 'ul') last.items.push(bullet[1]);
      else blocks.push({ t: 'ul', items: [bullet[1]] });
      return;
    }
    blocks.push({ t: 'p', text: line });
  });
  return blocks;
};

/** Fallback plain text for Copy when a message has no raw reply text; Copy prefers `Message.text`. */
export const blocksToPlainText = (blocks: MessageBlock[]): string =>
  blocks
    .map((block) => {
      if (block.t === 'p') return block.text;
      if (block.t === 'ol') return block.items.map((item, i) => `${(block.start ?? 1) + i}. ${item}`).join('\n');
      return block.items.map((item) => `- ${item}`).join('\n');
    })
    .join('\n\n');

export const ERROR_TEXT = "Mozi couldn't answer that. Try again in a moment.";
